import { isAdobeFontStylesheetUrl } from "@hightouch/lib/brand-profiles/assets/adobe-fonts";
import { HOSTED_FONT_LOAD_TIMEOUT_MS } from "src/pages/ad-studio/hooks/hosted-font-constants";

type StylesheetEntry = {
  link: HTMLLinkElement;
  ready: Promise<void>;
  users: number;
};

const stylesheets = new Map<string, StylesheetEntry>();

export function acquireHostedFontStylesheet(url: string): {
  ready: Promise<void>;
  release: () => void;
} {
  if (!isAdobeFontStylesheetUrl(url)) {
    throw new Error("Invalid Adobe web project URL");
  }
  let entry = stylesheets.get(url);
  if (!entry) {
    const link = document.createElement("link");
    link.rel = "stylesheet";
    link.href = url;
    const ready = new Promise<void>((resolve, reject) => {
      const timer = setTimeout(() => {
        link.onload = null;
        link.onerror = null;
        reject(new Error("Adobe font stylesheet took too long to load"));
      }, HOSTED_FONT_LOAD_TIMEOUT_MS);
      link.onload = () => {
        clearTimeout(timer);
        resolve();
      };
      link.onerror = () => {
        clearTimeout(timer);
        reject(new Error("Could not load Adobe font stylesheet"));
      };
    });
    entry = { link, ready, users: 0 };
    stylesheets.set(url, entry);
    document.head.appendChild(link);
  }
  entry.users += 1;
  const acquired = entry;
  let released = false;
  return {
    ready: entry.ready,
    release: () => {
      if (released) {
        return;
      }
      released = true;
      acquired.users -= 1;
      if (acquired.users === 0) {
        acquired.link.remove();
        stylesheets.delete(url);
      }
    },
  };
}
